import { Checkbox, Dialog, IconButton, List, ListItem, ListItemText, makeStyles, Paper, Theme, Tooltip } from '@material-ui/core';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import { CSSProperties } from '@material-ui/styles';
import { action } from 'mobx';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { IEntity } from '../main/entities';
import { transl } from '../main/transl';
import { getScrollbarSize } from '../mg/getScrollbarSize';
import { mg } from '../mg/mg';
import { useBooleanStateAgg } from '../mg/useStateAgg';
import { HarmoInfo } from '../pages/ges/harmo/HarmoInfo';
import { HarmoPrimarySecondary } from '../pages/ges/harmo/HarmoPrimarySecondary';
import { HarmoStatus } from '../pages/ges/harmo/HarmoStatus';
import { NameSeachComp } from '../pages/ges/obsolete/NameSearchComp';
import { IDataviewStore } from '../stores/DataviewStore';
import { IDataviewStoreColumn } from '../stores/DataviewStoreColumn';
import { AddToCart } from './AddToCart';
import { AmountToOrder } from './AmountToOrder';
import { IOcDatascrollInfo, OcDatascroll } from './OcDatascroll';
import { OcIsolator } from './OcIsolator';
import { PleaseWait } from './PleaseWait';
import { PricePair } from './PricePair';

const headerColor = '#FCFAF8';

const useStyles = makeStyles((theme: Theme) => ({
	sortIcon: {
		width: 16,
		height: 16,
		transition: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 100ms, transform 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
	},
	reversedIcon: {
		opacity: 0.8,
		transform: 'rotate(180deg)',
	},
	normalIcon: {
		opacity: 0.8,
		transform: 'rotate(0deg)',
	},
	noIcon: {
		opacity: 0,
	},
	headerCell: {
		cursor: 'pointer',
		height: 60,
		display: 'block',
	},
	toolbarLeft: {
		display: 'flex',
		flex: 1,
		justifyContent: 'flex-start',
		paddingLeft: 10,
		paddingTop: 10,
	},
	toolbarCenter: {
		display: 'flex',
		flex: 1,
		justifyContent: 'center',
		paddingRight: 30,
	},
	toolbarRight: {
		display: 'flex',
		flex: 1,
		justifyContent: 'flex-end',
		paddingRight: 30,
	},
	toolbar: {
		backgroundColor: headerColor,
		height: 65,
	},
	headerRow: {
		backgroundColor: headerColor,
		color: '#777',
		height: 64,
		borderTop: 'none',
		fontSize: 15,
		paddingRight: getScrollbarSize(),
	},
	headerRowInner: {
		display: 'flex',
		flexDirection: 'row',
		height: 60,
	},
	footerRow: {
		backgroundColor: headerColor,
		color: '#777',
		height: 32,
		fontSize: 15,
	},
	footerLeftRight: {
		display: 'flex',
		flex: 1,
		padding: 10,
		alignItems: 'center',
	},
	footerLeft: {
		justifyContent: 'start',
	},
	footerCenter: {
		justifyContent: 'center',
	},
	footerRight: {
		justifyContent: 'end',
	},
	span: {
		display: 'table-cell',
		verticalAlign: 'middle',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		hyphens: 'auto',
		overflowWrap: 'anywhere',
		height: 48,
	},
	cell: {
		paddingLeft: 2,
		paddingRight: 2,
		overflow: 'hidden',
	},
	disabled: {
		color: '#EE0000',
	},
	normalColor: {
		color: '#555',
	},
	emphasis: {
		color: '#6200EE',
	},
	datarow: {
		borderTop: '1px solid #CCC',
		height: 48,
	},
	row: {
		display: 'flex',
	},
	header: {
		flex: 0,
	},
	footer: {
		flex: 0,
	},
	body: {
		flex: 1,
		visibility: 'hidden',
	},
	table: {
		fontWeight: 400,
		fontSize: '15px',
		textAlign: 'center',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		width: '100%',
		flex: 1,
	},
	stickyTopRow: {
		paddingRight: getScrollbarSize(),
		backgroundColor: '#DDD',
		'& > div': {
			// borderRight: '1px solid #CCC',
			// borderBottom: '1px solid #CCC',
			border: 'none',
		},
		'& > div > div': {
			// borderLeft: '1px solid #CCC',
			color: theme.palette.secondary.main,
		}
	},
	flex1: {
		flex: 1,
	},
}));

const SortIcon = observer(function(props: {normal: boolean, reversed: boolean}) {
	const {normal, reversed} = props;
	const classes = useStyles();
	const className = mg.clsn(classes.sortIcon, reversed ? classes.reversedIcon : normal ? classes.normalIcon : classes.noIcon);
	return <svg className={className} focusable="false" viewBox="0 0 24 24" aria-hidden="true" role="presentation">
		<path d="M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z"></path>
	</svg>;
});

interface IOcDataviewHeaderCellProps {
	state: IDataviewStore;
	column: IDataviewStoreColumn;
	unitWidth: number;
	key: string;
}

const OcDataviewHeaderCell = observer(function(props: IOcDataviewHeaderCellProps) {
	const {column, state, unitWidth} = props;
	const onClick = action(() => state.sorting.columnClick(column));
	const classes = useStyles();

	const sorting = state.sorting;
	const isSorting = sorting.column === column.name;
	const className = mg.clsn(classes.cell, classes.headerCell);
	const divStyle = {flex: column.widthDesc};
	switch (column.type) {
	case 'multiselect': return <div style={divStyle}/>;
	case 'addToCart': return <div style={divStyle}/>;
	}
	return <div style={divStyle} className={className} onClick={onClick}>
		<SortIcon normal={isSorting} reversed={isSorting && sorting.reversed}/>
		<div>{transl(column.name)}</div>
	</div>;
});

interface IOcDataviewToolbarCellProps {
	name: string;
	children: React.ReactNode;
	onClick?: React.MouseEventHandler<HTMLElement>;
}

const OcDataviewToolbarCell = observer(function(props: IOcDataviewToolbarCellProps) {
	const {name, onClick, children} = props;
	return <Tooltip title={transl(name)}>
		<IconButton aria-label={transl('name')} onClick={onClick}>
			{children}
		</IconButton>
	</Tooltip>;
});

interface IOcDataviewHeaderProps {
	state: IDataviewStore;
}

const OcDataviewHeader = observer(function(props: IOcDataviewHeaderProps) {
	const classes = useStyles();
	const {state} = props;
	const unitWidth = state.unitWidth;
	const params = {state, unitWidth};
	return <OcIsolator
		outerStyle={{height: '64px'}} //TODO
		outerClassName={mg.clsn(classes.row, classes.headerRow)}
		innerClassName={mg.clsn(classes.row, classes.headerRow, classes.headerRowInner)}>
		{state.visibleColumns.map(c => <OcDataviewHeaderCell key={c.name} column={c} {...params}/>)}
	</OcIsolator>;
});

interface IOcDataviewFooterProps {
	state: IDataviewStore
	info: IOcDatascrollInfo
}

const OcDataviewFooter = observer(function(props: IOcDataviewFooterProps) {
	const editColumnsAgg = useBooleanStateAgg();
	const classes = useStyles();
	const {info, state} = props;
	return <div className={mg.clsn(classes.row, classes.footerRow)}>
		<div className={mg.clsn(classes.row, classes.footerLeftRight, classes.footerLeft)}>
			<OcDataviewToolbarCell name="editColumns" onClick={editColumnsAgg.trueHandler}>
				<ViewColumnIcon/>
			</OcDataviewToolbarCell>
		</div>
		{
			!info.nRows ? null :
				<div className={mg.clsn(classes.row, classes.footerLeftRight, classes.footerCenter)}>
					{info.firstVisibleRowIndex+1} - {info.lastVisibleRowIndex+1} / {info.nRows}
				</div>
		}
		<div className={mg.clsn(classes.row, classes.footerLeftRight, classes.footerRight)}/>
		<Dialog open={editColumnsAgg.value} onClose={editColumnsAgg.falseHandler}>
			<List>{state.availableColumns.map(c =>
				<ListItem key={c.name} dense button disableRipple onClick={e => c.toggleColumnHider('DEFAULT')}>
					<Checkbox checked={!!~state.visibleColumns.indexOf(c)} color="primary" tabIndex={-1} disableRipple/>
					<ListItemText primary={transl(c.name)}/>
				</ListItem>
			)
			}</List>
		</Dialog>
	</div>;
});

type IOnEdit = (entity: {[s: string]: any}, changes: {[s: string]: any}) => void

interface IOcDataviewCellProps {
	column: IDataviewStoreColumn;
	state: IDataviewStore;
	entity: IEntity;
	onEdit?: IOnEdit
	unitWidth: number;
}

const OcDataviewCell = observer(function(props: IOcDataviewCellProps) {
	const classes = useStyles();
	const {column, entity, state, onEdit, unitWidth} = props;
	const divStyle: CSSProperties = {flex: column.widthDesc,cursor:'pointer'};
	const toggle = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
		event.preventDefault();
		event.stopPropagation();
		state.toggleMultiselected(entity.id);
	};
	function renderContent() {
		switch (column.type) {
		case 'multiselect': return <Checkbox color="primary" tabIndex={-1} disableRipple
			checked={state.isMultiselected(entity.id)}
			onClick={toggle}
		/>;
		case 'amountToOrder': return <AmountToOrder entity={entity as any} state={state} onEdit={onEdit}/>;
		case 'pricePair': return <PricePair entity={entity as object}/>;
		case 'nameSearch': return <NameSeachComp entity={entity} columnName={column.name}/>;
		case 'addToCart': return <AddToCart entity={entity}/>;
		case 'harmoPrimarySecondary': return <HarmoPrimarySecondary entity={entity} state={state}/>;
		case 'harmoInfo': return <HarmoInfo entity={entity as any} state={state}/>;
		case 'harmoStatus': return <HarmoStatus entity={entity as any} state={state}/>;
		}
		return column.formatForView(entity);
	}
	return <div style={divStyle} className={mg.clsn('dataview-cell', classes.cell)}>{renderContent()}</div>;
});

interface IOcDataviewRowProps {
	state: IDataviewStore
	entity: IEntity
	lineStyle?(entity: IEntity) : CSSProperties | undefined
	onEdit?: IOnEdit
}

const OcDataviewRow = observer(function(props: IOcDataviewRowProps) {
	const {state, entity, lineStyle, onEdit} = props;
	const classes = useStyles();
	const isDraggingAgg = useBooleanStateAgg();
	const onClick = action(() => !isDraggingAgg.value && state.rowClick(entity.id));

	const unitWidth = state.unitWidth;
	const highlighting = state.highlighting;
	const highlightingCol = highlighting.replace(/^!/, '');
	const highlightingNeg = highlighting !== highlightingCol;
	const emphasisclassName = state.disabling && (entity as any)[state.disabling] ? classes.disabled :
		state.highlighting && (!highlightingNeg !== !(entity as any)[highlightingCol]) ? classes.emphasis : classes.normalColor;
	const style = lineStyle?.(entity);
	return <div
		className={mg.clsn(emphasisclassName, classes.row, classes.datarow)}
		style={style}
		onMouseDown={isDraggingAgg.falseHandler} onMouseMove={isDraggingAgg.trueHandler} onClick={onClick}
	>
		{state.visibleColumns.map(c => <OcDataviewCell key={c.name} column={c} entity={entity} unitWidth={unitWidth} onEdit={onEdit} state={state}/>)}
	</div>;
});

export interface IOcDataviewProps {
	state: IDataviewStore
	stickyTopEntity?: IEntity
	repo: {
		allAsList: Array<IEntity>
		etag: string
		onEdit?: IOnEdit
	}
	paperStyle?: any
	className?: string
	emptyRenderer?() : JSX.Element
	lineStyle?(entity: IEntity) : CSSProperties | undefined
}

export const OcDataview = observer(function(props: IOcDataviewProps) {
	const classes = useStyles();
	const {repo, state, className, paperStyle, emptyRenderer: emptyRenderer2, stickyTopEntity, lineStyle} = props;
	const rows = state.applyTo(repo.allAsList);
	const datascrollBounds = useLocalStore(() => ({} as React.ComponentProps<typeof OcDatascroll>['bounds']));
	const datascrollInfo: IOcDatascrollInfo = useLocalStore(OcDatascroll.emptyInfo);

	const onResize = action((bounds: React.ComponentProps<typeof OcDatascroll>['bounds']) => {
		Object.assign(datascrollBounds, bounds);
		props.state.setRowWidth(datascrollInfo.rowWidth);
	});
	const onDatascrollChange = action((info: IOcDatascrollInfo) => {
		Object.assign(datascrollInfo, info);
		props.state.setRowWidth(datascrollInfo.rowWidth);
	});

	const emptyRenderer = emptyRenderer2 ?? (() => <div style={{width: '100%', height: '100%', display: 'table'}}>
		<span style={{display: 'table-cell', textAlign: 'center', verticalAlign: 'middle'}}>
			{transl('noData')}
		</span>
	</div>); //TODO style=
	const rowRenderer = (e: IEntity) => {
		return <OcDataviewRow state={state} entity={e} lineStyle={lineStyle} onEdit={repo.onEdit}/>;
	};

	return <Paper className={mg.clsn(className, classes.table)} style={paperStyle}>
		<OcDataviewHeader state={state}/>
		{
			!stickyTopEntity ? null : <div className={classes.stickyTopRow}>
				<OcDataviewRow
					state={state}
					entity={stickyTopEntity}
				/>
			</div>
		}
		<OcIsolator dbgName="OcDataview-body" onResize={onResize} outerClassName={classes.flex1}>
			{
				!repo.etag ? <PleaseWait/>
					: <OcDatascroll rows={rows}
						state={state}
						bounds={datascrollBounds}
						minRowHeight={48}
						onChange={onDatascrollChange}
						rowRenderer={rowRenderer}
						emptyRenderer={emptyRenderer}
					/>
			}
		</OcIsolator>
		<OcDataviewFooter state={state} info={datascrollInfo}/>
	</Paper>;
});
